import { Box, Button, Checkbox, FormControlLabel, TextField, Tooltip } from "@mui/material";
import { SyntheticEvent, useState } from "react";
import { useSaveGroupMutation } from "../../../App/Queries/GroupQueries";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { IFeatureToggles } from "../../../App/Typings/FeatureToggles";
import { useNavigate } from "react-router-dom";
import Modal from "../../../App/Components/Modal/Modal";

interface ICreateGroupProps {
  isCreatingGroup: boolean;
  isCreatingGroupError: boolean;
  setIsCreatingGroup: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCreatingGroupError: React.Dispatch<React.SetStateAction<boolean>>;
  handleToggleCreateGroup: () => void;
}

export function CreateGroup({
  isCreatingGroup,
  setIsCreatingGroup,
  isCreatingGroupError,
  setIsCreatingGroupError,
  handleToggleCreateGroup,
}: ICreateGroupProps) {
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isNewGroupDynamic, setIsNewGroupDynamic] = useState<boolean>(false);
  const { displaySuccess, displayError } = useNotification();
  const accessCreateGroupButton = useFeaturePermission(AppFeatureLookup.GroupsCreateGroup);
  const { enabledDynamicGroups } = useFlags<IFeatureToggles>();
  const saveGroupMutation = useSaveGroupMutation("");
  const navigate = useNavigate();

  const handleGroupSave = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!accessCreateGroupButton) {
      return;
    }
    if (isCreatingGroupError) {
      displayError("Group names must be greater than 3 and less than or equal to 100 characters");
      return;
    }
    setIsSaving(true);
    saveGroupMutation.mutate(
      {
        name: newGroupName,
        members: [],
        isDynamic: isNewGroupDynamic,
        rules: JSON.stringify([]),
      },
      {
        onError: () => {
          displayError("There was an error creating the group. Please try again!");
          setIsSaving(false);
        },
        onSuccess: (group) => {
          setNewGroupName("");
          setIsCreatingGroup(false);
          setIsNewGroupDynamic(false);
          displaySuccess("New group added successfully!");
          setIsSaving(false);
          if (isNewGroupDynamic && group) {
            navigate(`/candidategroups/edit/${group.id}`);
          } else {
            handleToggleCreateGroup();
          }
        },
      },
    );
  };

  const handleInputNewGroupName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    validateGroupName(value);
    setNewGroupName((_) => value);
  };

  const validateGroupName = (value: string) => {
    if (value.length < 3 || value.length > 100) {
      setIsCreatingGroupError(true);
    }
    if (isCreatingGroupError && value.length <= 100 && value.length >= 3) {
      setIsCreatingGroupError(false);
    }
  };

  return (
    <>
      <Tooltip title={accessCreateGroupButton ? "Create a Group" : crmLiteUpgradeMessage} arrow>
        <Box sx={{ height: "100%" }}>
          <Button
            sx={{ height: "100%" }}
            fullWidth
            type="button"
            variant="contained"
            color="primary"
            data-test="creategroup-openmodal"
            onClick={handleToggleCreateGroup}
            aria-controls="create-candidate-group"
            aria-haspopup="true"
            disabled={!accessCreateGroupButton}
          >
            Create Group
          </Button>
        </Box>
      </Tooltip>
      <Modal
        isOpen={isCreatingGroup}
        dataTestId="create-candidate-group"
        handleCloseModal={handleToggleCreateGroup}
        title="Create Group"
        key={"create-candidate-group"}
        content={
          <>
            <TextField
              data-test="creategroup-nameinput"
              label="Enter Group Name:"
              value={newGroupName}
              fullWidth={true}
              helperText={`Group names must be at least 3 characters long, and less than 100 characters. ${newGroupName.length}/100`}
              onChange={handleInputNewGroupName}
              error={isCreatingGroupError}
            ></TextField>
            {enabledDynamicGroups && (
              <Tooltip
                title="Dynamic groups are used to create automatic rule based list of candidates"
                placement="right-start"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      data-test="creategroup-dynamic-checkbox"
                      title="Is this group dynamic?"
                      value={isNewGroupDynamic}
                      onChange={() => setIsNewGroupDynamic(!isNewGroupDynamic)}
                    />
                  }
                  label="Is this group dynamic?"
                />
              </Tooltip>
            )}
          </>
        }
        footerContent={
          <>
            <Button
              data-test="creategroup-savegroup"
              type="submit"
              variant="contained"
              disabled={newGroupName.length === 0 || isCreatingGroupError || isSaving}
              onClick={handleGroupSave}
            >
              Save
            </Button>
          </>
        }
      />
    </>
  );
}
